import { Editor as ToastEditor } from "@toast-ui/react-editor";
import Board from "components/Common/Board/Board";
import DragAndDropInput from "components/Common/DragAndDropInput/DragAndDropInput";
import { FilterListLayout } from "components/Common/PageFilterPannel/FilterListItem";
import Editor from "components/document/Create/Editor";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import Modal from "components/foundation/Modal/Modal";
import Toast from "components/foundation/Toast";
import { ROUTER_PATH } from "config/path";
import useBeforeUnload from "features/common/hooks/useBeforeUnload";
import useNoticeMutation from "features/notice/hooks/mutations/useNoticeMutation";
import usePresignedUrlMutation from "features/upload/hooks/mutations/usePresignedUrlMutation";
import SaveWarningModal from "pages/user/dashboard/detail/SaveWarningModal";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import calculateMD5 from "utils/fileUtils/calculateMD5";
import removeEmptyFields from "utils/removeEmptyFields";

const NoticeCreateWebPage = () => {
  const navigate = useNavigate();
  const {
    blocking,
    process,
    isBlock,
    enableUnloadTracking,
    disableUnloadTracking,
  } = useBeforeUnload({
    isInitialTracking: false,
  });

  const [nameErrorToastOpen, setNameErrorToastOpen] = React.useState(false);
  const [title, setTitle] = React.useState("");
  const [isFileErrorModal, setIsFileErrorModal] = React.useState(false);
  const [isConfirmModal, setIsConfirmModal] = React.useState(false);
  const [isNameEmptyOpen, setIsNameEmptyOpen] = React.useState(false);
  const [isContentEmptyOpen, setIsContentEmptyOpen] = React.useState(false);
  const [file, setFile] = useState<File>();
  const editorRef = React.useRef<ToastEditor | null>(null);

  const { mutate } = useNoticeMutation();
  const { mutate: imageUpload } = usePresignedUrlMutation({});

  const handleSubmit = async () => {
    if (!editorRef.current) return;
    if (title.length < 2) return;
    if (title.length > 100) {
      setNameErrorToastOpen(true);
      return;
    }

    const editorInstance = editorRef.current.getInstance();
    const value = editorInstance?.getHTML() || "";

    let url = undefined;
    let noticeAttachmentFileList:
      | [
          {
            fileHash: string;
            fileName: string;
            noticeFileUrl: string;
          },
        ]
      | [] = [];

    if (file) {
      url = await imageUpload(file, {
        fileName: file.name,
        fileType: "NOTICE",
        isOverwrite: true,
        allowedExtensions: ["." + file.name.split(".").pop() ?? ""],
      });
      const attachmentFileMD5 = await calculateMD5(file);

      noticeAttachmentFileList = [
        {
          fileHash: attachmentFileMD5,
          fileName: file.name,
          noticeFileUrl: url,
        },
      ];
    }

    mutate(
      removeEmptyFields({
        title,
        content: value,
        noticeAttachmentFileList,
        isMobile: false,
      }),

      {
        onSuccess: (data) => {
          disableUnloadTracking();
          navigate(
            ROUTER_PATH.notice.dashboard.detail(data.data.result.notice.uuid),
          );
        },
      },
    );
  };

  useEffect(() => {
    (title.length > 0 ||
      file ||
      (editorRef.current?.getInstance().getSelectedText().length ?? 0) > 1) &&
      enableUnloadTracking();
    return () => disableUnloadTracking();
  }, [disableUnloadTracking, enableUnloadTracking, file, title.length]);

  const handleConfirmClick = () => {
    const content = editorRef.current?.getInstance().getMarkdown();

    if (title.length > 100) {
      setNameErrorToastOpen(true);
    } else if (title.length === 0) {
      setIsNameEmptyOpen(true);
    } else if (!content) {
      setIsContentEmptyOpen(true);
    } else {
      setIsConfirmModal(true);
    }
  };

  return (
    <NoticeCreatePageLayout>
      <Toast.Message
        open={nameErrorToastOpen}
        onOpenChange={setNameErrorToastOpen}
      >
        제목은 최대 100자까지 입력이 가능합니다.
      </Toast.Message>
      <Modal.Root open={isConfirmModal} onOpenChange={setIsConfirmModal}>
        <Modal.Body
          title="공지사항을 등록하시겠습니까?"
          onConfirm={handleSubmit}
          onCancel
        />
      </Modal.Root>
      <Modal.Root open={isNameEmptyOpen} onOpenChange={setIsNameEmptyOpen}>
        <Modal.Body title="제목 기입 필수" onConfirm>
          제목은 필수 항목 입니다. 제목 기입 후 다시 등록해 주세요.
        </Modal.Body>
      </Modal.Root>
      <Modal.Root
        open={isContentEmptyOpen}
        onOpenChange={setIsContentEmptyOpen}
      >
        <Modal.Body title="내용 기입 필수" onConfirm>
          내용은 필수 항목 입니다. 내용 기입 후 다시 등록해 주세요.
        </Modal.Body>
      </Modal.Root>

      <Modal.Root open={isFileErrorModal} onOpenChange={setIsFileErrorModal}>
        <Modal.Body
          title="30MB 이하 파일만 등록 가능합니다."
          onConfirm={() => setIsFileErrorModal(false)}
        >
          파일 용량을 확인해 주세요.
        </Modal.Body>
      </Modal.Root>
      <SaveWarningModal
        isOpen={isBlock}
        onConfirm={process}
        onCancel={blocking}
      />
      <NoticeCreatePageLayoutHeader>
        <h1>공지사항 등록</h1>
        <div
          style={{
            display: "flex",
            gap: "1.6rem",
          }}
        >
          <Link to={ROUTER_PATH.notice.dashboard._path}>
            <Button size="large" variant="tertiary">
              뒤로
            </Button>
          </Link>
          <Button size="large" type="submit" onClick={handleConfirmClick}>
            등록
          </Button>
        </div>
      </NoticeCreatePageLayoutHeader>
      <NoticeCreateContentBoard>
        <NoticeInput
          placeholder="제목"
          value={title}
          onChange={(e) => {
            if (e.target.value.length > 100) {
              setNameErrorToastOpen(true);
            }
            setTitle(e.target.value);
          }}
        />
        <Divider />
        <Editor
          placeholder="본문 내용을 입력해 주세요."
          ref={editorRef}
          initialValue=""
        />
      </NoticeCreateContentBoard>
      <NoticeCreateAttachBoard>
        <FilterListLayout>
          <h2>첨부파일</h2>
          <div>
            <DragAndDropInput
              maxAttachments={1}
              onChangeCallback={(files) => setFile(files[0])}
            />
          </div>
        </FilterListLayout>
      </NoticeCreateAttachBoard>
    </NoticeCreatePageLayout>
  );
};

export default NoticeCreateWebPage;

const NoticeCreatePageLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const NoticeCreateContentBoard = styled(Board)`
  padding: 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  .toastui-editor-ww-container {
    background-color: ${({ theme }) => theme.color.background.white} !important;
  }
`;

const NoticeCreateAttachBoard = styled(Board)`
  padding: 4rem;
`;

const NoticeCreatePageLayoutHeader = styled(Board)`
  padding: 3rem 4rem;
  display: flex;
  justify-content: space-between;

  h1 {
    font-size: 28px;
    font-weight: 600;
    line-height: 36px; /* 128.571% */
    letter-spacing: -0.56px;
  }
`;

const NoticeInput = styled.input`
  border: none;
  outline: none;
  font-size: 24px;
  font-weight: 600;
  line-height: 36px; /* 150% */
  letter-spacing: -0.72px;

  ::placeholder {
    color: ${({ theme }) => theme.color.text.placeholder};
  }
`;
